import sectionsFixtures from "./sectionsFixtures";
import statusCodes from "./statusCodes";
import { getByPath, isEmpty } from "./utils";
import { isCategoryCodeABM } from "./helpers";
import { getSortedSections } from "../containers/Profile";

const { COMPLETED, CONTINUAL, NOT_STARTED } = statusCodes;

const areFieldsFilled = (section, cndData) => {
  return section.fields.every((path) => {
    const dependency = section.dependencies && section.dependencies[path];
    if (
      dependency &&
      getByPath(dependency.field, cndData) !== dependency.allowedValue
    ) {
      return true;
    }
    return !isEmpty(getByPath(path, cndData));
  });
};

export const addSections = (cndData) => {
  const sections = sectionsFixtures.sections.map((section) => {
    let status;
    const disabled = false;
    if (section.status === "CONTINUAL") {
      status = CONTINUAL;
    } else if (
      areFieldsFilled(section, cndData) &&
      section.taskName !== "Onboarding"
    ) {
      status = COMPLETED;
    } else {
      status = NOT_STARTED;
    }

    return {
      ...section,
      status,
      disabled,
    };
  });

  // Add cv description based on cv file
  if (Object.prototype.hasOwnProperty.call(cndData, "cv")) {
    const SECTION_CV_ID = 1;
    const cvSection = sections[SECTION_CV_ID];
    const section = {
      ...cvSection,
      description: cndData.cv.fileName,
    };
    sections[SECTION_CV_ID] = section;
  }

  return { ...cndData, sections };
};

const toggleProfileSectionDisplay = (
  sectionName,
  shouldBeHiddenFn,
  by = "taskName"
) => {
  return (cndData) => {
    if (shouldBeHiddenFn(cndData)) {
      const modifiedSections = cndData.sections.map((section) => {
        if (getByPath(by, section) === sectionName) {
          return {
            ...section,
            isHidden: true,
          };
        }
        return section;
      });

      return {
        ...cndData,
        sections: modifiedSections,
      };
    }

    return cndData;
  };
};

export const hideGLAASection = toggleProfileSectionDisplay(
  "stronger-together",
  ({ permissions }) => !permissions || !permissions.isGLAA,
  "link"
);

const hasCompletedSection =
  (sectionName) =>
  ({ sections }) =>
    sections.find((section) => section.taskName === sectionName).status ===
    COMPLETED;
const hasCompletedPayroll = hasCompletedSection("PayrollInformation");

export const hidePayrollSection = toggleProfileSectionDisplay(
  "PayrollInformation",
  (cndData) => {
    const payrollStatus = cndData.sections.reduce((_acc, _section) =>
      _section.taskName === "PayrollInformation" ? _section.status : _acc
    );
    const shouldHide =
      !hasCompletedPayroll(cndData) &&
      cndData.permWorkSeeking &&
      !cndData.tempWorkSeeking;
    return payrollStatus === COMPLETED ? false : shouldHide;
  }
);

export const hideTimesheetsSection = toggleProfileSectionDisplay(
  "Timesheets",
  ({ permissions }) => !permissions || !permissions.seeTimesheets
);

export const hideBookingsSection = toggleProfileSectionDisplay(
  "Bookings",
  ({ permissions }) => !permissions || !permissions.seeBookings
);

export const hidePayslipsSection = toggleProfileSectionDisplay(
  "Payslips",
  ({ permissions }) => !permissions || !permissions.seePayslips
);

export const hideRAOnboardingSection = toggleProfileSectionDisplay(
  "Onboarding",
  (cndData) => cndData.responsibleTeam !== "LIVX" || !cndData.tempWorkSeeking,
  "link"
);

export const hideGreatBearSection = toggleProfileSectionDisplay(
  "OnboardingGB",
  ({ permissions }) => !permissions || !permissions.isGreatBear,
  "link"
);

export const hideRoyalMailOnboardingSection = toggleProfileSectionDisplay(
  "royal-mail-onboarding",
  ({ permissions }) => !permissions || !permissions.isRoyalMail,
  "link"
);

export const hideDnataOnboardingSection = toggleProfileSectionDisplay(
  "dnata-onboarding",
  ({ permissions }) => !permissions || !permissions.isDnata,
  "link"
);

export const hideSwissportOnboardingSection = toggleProfileSectionDisplay(
  "swissport-onboarding",
  ({ permissions }) => !permissions || !permissions.isSwissport,
  "link"
);

export const hideNHSReferencesFormSection = toggleProfileSectionDisplay(
  "nhs-referencing",
  ({ permissions }) => !permissions || !permissions.isNHS,
  "link"
);

export const hideUmbrellaDeclarationSection = toggleProfileSectionDisplay(
  "umbrella-declaration",
  (cndData) => {
    return !cndData.umbrellaDeclarationForm;
  },
  "link"
);

export const hideGateOnboardingSection = toggleProfileSectionDisplay(
  "gate-group-onboarding",
  ({ permissions }) => !permissions || !permissions.isGate,
  "link"
);

export const hideABMOnboardingSection = toggleProfileSectionDisplay(
  "ABM-onboarding",
  (cndData) => {
    return !isCategoryCodeABM(cndData.complianceCategoryCode);
  },
  "link"
);

export const hideCarltonForestOnboardingSection = toggleProfileSectionDisplay(
  "carlton-forest-onboarding",
  ({ permissions }) => !permissions || !permissions.isCarltonForest,
  "link"
);

export const hideCPIOnboardingSection = toggleProfileSectionDisplay(
  "cpi-onboarding",
  ({ permissions }) => !permissions || !permissions.isCPI,
  "link"
);

export const hideThanetCouncilOnboardingSection = toggleProfileSectionDisplay(
  "thanet-council-onboarding",
  ({ permissions }) => !permissions || !permissions.isThanetCouncil,
  "link"
);

export const hideKingspanSection = toggleProfileSectionDisplay(
  "kingspan-onboarding",
  ({ permissions }) => !permissions || !permissions.isKingspan,
  "link"
);

export const hideRegattaSection = toggleProfileSectionDisplay(
  "regatta-onboarding",
  ({ permissions }) => !permissions || !permissions.isRegatta,
  "link"
);

export const hidePecanSection = toggleProfileSectionDisplay(
  "onboarding-pecan",
  ({ permissions }) => !permissions || !permissions.isPecan,
  "link"
);

export const hidePusterlaSection = toggleProfileSectionDisplay(
  "pusterla-onboarding",
  ({ permissions }) => !permissions || !permissions.isPusterla,
  "link"
);

export const hideDrivingOnboardingSection = toggleProfileSectionDisplay(
  "driving-onboarding",
  ({ permissions }) => !permissions || !permissions.isLookingForDrivingJob,
  "link"
);

export const hideAddressSection = toggleProfileSectionDisplay(
  "Address",
  (cndData) =>
    cndData.sections.find((e) => e.name === "Address").status === COMPLETED,
  "name"
);

export const hideProfileImageSection = toggleProfileSectionDisplay(
  "ProfileImage",
  (cndData) => {
    return (
      cndData.sections.find((e) => e.name === "Profile Image").status ===
      COMPLETED
    );
  }
);

export const hideMobileTelephoneSection = toggleProfileSectionDisplay(
  "Mobile Telephone Number",
  (cndData) =>
    cndData.sections.find((e) => e.name === "Mobile Telephone Number")
      .status === COMPLETED,
  "name"
);

export const hideBirthDateSection = toggleProfileSectionDisplay(
  "Date of Birth",
  (cndData) =>
    cndData.sections.find((e) => e.name === "Date of Birth").status ===
    COMPLETED,
  "name"
);

export const hideHolidaySection = toggleProfileSectionDisplay(
  "Holiday",
  ({ permissions }) => !permissions || !permissions.seePayslips
);

export const hideMolecularOnboardingSection = toggleProfileSectionDisplay(
  "molecular-onboarding",
  ({ permissions }) => !permissions || !permissions.isMolecular,
  "link"
);

export const hideWoodlandOnboardingSection = toggleProfileSectionDisplay(
  "woodland-group-onboarding",
  ({ permissions }) => !permissions || !permissions.isWoodland,
  "link"
);

export const hideCompleatFoodOnboardingSection = toggleProfileSectionDisplay(
  "compleat-food-onboarding",
  ({ permissions }) => !permissions || !permissions.isCompleatFood,
  "link"
);

export const hideGrowUpFarmsOnboardingSection = toggleProfileSectionDisplay(
  "grow-up-farms-onboarding",
  ({ permissions }) => !permissions || !permissions.isGrowUpFarms,
  "link"
);

export const hideDavinFoodsOnboardingSection = toggleProfileSectionDisplay(
  "davin-foods-onboarding",
  ({ permissions }) => !permissions || !permissions.isDavinFoods,
  "link"
);
export const completeGLAASection = (cndData) => {
  const isComplete = cndData.glaaOnboarding && cndData.glaaOnboarding.YS01;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "stronger-together") {
      return {
        ...section,
        status: isComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeRAOnboardingSection = (cndData) => {
  const filenameFromUrl = (url) => url.split("/").slice(-1)[0];
  const readingList = window._umbraco_readingList;
  const readDocuments =
    (cndData.reflexAllen && cndData.reflexAllen.readDocuments) || [];
  const readingListWithStatus = readingList
    ? readingList.map((download) => ({
        ...download,
        status: !!readDocuments.includes(filenameFromUrl(download.link)),
      }))
    : [];
  const isReadingListComplete = readingListWithStatus.every(
    (list) => list.status === true
  );
  const isInductionFormComplete =
    getByPath("reflexAllen.inductionForm.uncheckedItems", cndData) &&
    cndData.reflexAllen.inductionForm.uncheckedItems.length === 0 &&
    cndData.reflexAllen.inductionForm.checkedItems.length !== 0;

  const regulationsExistInUserData =
    getByPath("reflexAllen.onlineForms", cndData) &&
    cndData.reflexAllen.onlineForms.includes("regulations-form");

  const nightWorkerrsExistInUserData =
    getByPath("reflexAllen.onlineForms", cndData) &&
    cndData.reflexAllen.onlineForms.includes(
      "night-workers-health-questionnaire"
    );

  const areFormsComplete =
    regulationsExistInUserData && nightWorkerrsExistInUserData;
  if (isReadingListComplete && isInductionFormComplete && areFormsComplete) {
    const updatedSections = cndData.sections.map((section) => {
      if (section.link === "Onboarding") {
        return {
          ...section,
          status: COMPLETED,
        };
      }
      return section;
    });
    return {
      ...cndData,
      sections: updatedSections,
    };
  }

  return cndData;
};

export const completeGreatBearOnboardingSection = (cndData) => {
  const isReadingListComplete =
    cndData.greatBear &&
    cndData.greatBear.readDocuments &&
    cndData.greatBear.readDocuments.includes(
      "site-induction-pack-october-2021.pdf"
    );

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "OnboardingGB") {
      return {
        ...section,
        status: isReadingListComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeKingspanOnboardingSection = (cndData) => {
  const isReadingListComplete =
    cndData.kingspanOnboarding &&
    cndData.kingspanOnboarding.YKN1 &&
    cndData.kingspanOnboarding.readDocuments &&
    cndData.kingspanOnboarding.readDocuments.includes(
      "holywell-agency-induction-2022.pdf"
    ) &&
    cndData.kingspanOnboarding.readDocuments.includes(
      "manual_handling_handbook.pdf"
    ) &&
    cndData.kingspanOnboarding.readDocuments.includes(
      "drug-and-alcohol-policy-agency-workers-v2.pdf"
    ) &&
    cndData.kingspanOnboarding.readDocuments.includes(
      "gn-sop-009-isolation-procedure-agency.pdf"
    );

  const isSOPSigned = false;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "kingspan-onboarding") {
      return {
        ...section,
        status: isReadingListComplete && isSOPSigned ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeCarltonForestOnboardingSection = (cndData) => {
  const isReadingListComplete =
    cndData.carltonForestOnboarding &&
    cndData.carltonForestOnboarding.readDocuments &&
    cndData.carltonForestOnboarding.readDocuments.includes(
      "agency-manual-handling-pp.pdf"
    ) &&
    cndData.carltonForestOnboarding.readDocuments.includes(
      "induction-agency.pdf"
    );

  const isSSOWSigned = false;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "carlton-forest-onboarding") {
      return {
        ...section,
        status: isReadingListComplete && isSSOWSigned ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeThanetCouncilOnboardingSection = (cndData) => {
  const thanetOnboarding = cndData.thanetCouncilOnboarding;

  const readingList = [
    "tdc-toolbox-talks-hr-go.pdf",
    "staff-induction-booklet-update-15082022.pdf",
  ];

  const nonDriverFormsList = [
    "benefitsDeclaration",
    "inductionSignoff",
    "shiftInduction",
    "YTDO",
  ];

  const driverFormsList = [
    "benefitsDeclaration",
    "driverDeclaration",
    "inductionSignoff",
    "shiftInduction",
    "dvlaCheckAndSignature",
    "YTDO",
  ];

  const driverRiskAssessmentsList = [
    "Driving Company Vehicles",
    "Refuelling Operations",
    "Tipping of Vehicles",
    "Violence at Work",
    "Sun Exposure",
    "Lone Working",
    "General Manual Handling Activities",
    "Environmental Spillage",
    "Collection of Blue Boxes & Red Bags",
    "Collection & Disposal of Seagull Proof Bags & Black Sacks",
    "Collection & Disposal of Green Waste",
    "Blood Borne Viruses",
  ];
  const nonDriverRiskAssessmentsList = [
    "Violence at Work",
    "Sun Exposure",
    "Lone Working",
    "General Manual Handling Activities",
    "Environmental Spillage",
    "Collection of Blue Boxes & Red Bags",
    "Collection & Disposal of Seagull Proof Bags & Black Sacks",
    "Collection & Disposal of Green Waste",
    "Blood Borne Viruses",
  ];

  const isReadingListComplete =
    thanetOnboarding &&
    thanetOnboarding.readDocuments &&
    readingList.every((docName) =>
      thanetOnboarding.readDocuments.includes(docName)
    );

  const isNonDriversFormsListComplete =
    thanetOnboarding &&
    nonDriverFormsList.every((docName) => docName in thanetOnboarding);

  const isDriversFormsListComplete =
    thanetOnboarding &&
    driverFormsList.every((docName) => docName in thanetOnboarding);

  const isDriverRiskAssessmentsComplete =
    thanetOnboarding &&
    thanetOnboarding.riskAssessmentDriver &&
    driverRiskAssessmentsList.every(
      (docName) => docName in thanetOnboarding.riskAssessmentDriver
    );

  const isNonDriverRiskAssessmentsComplete =
    thanetOnboarding &&
    thanetOnboarding.riskAssessmentNonDriver &&
    nonDriverRiskAssessmentsList.every(
      (docName) => docName in thanetOnboarding.riskAssessmentNonDriver
    );

  const formsToComplete = () => {
    if (isDriverRiskAssessmentsComplete) return isDriversFormsListComplete;
    return isNonDriversFormsListComplete;
  };

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "thanet-council-onboarding") {
      return {
        ...section,
        status:
          isReadingListComplete &&
          formsToComplete() &&
          (isDriverRiskAssessmentsComplete ||
            isNonDriverRiskAssessmentsComplete)
            ? COMPLETED
            : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeCPIOnboardingSection = (cndData) => {
  const isReadingListComplete =
    cndData.cpiOnboarding &&
    cndData.cpiOnboarding.readDocuments &&
    cndData.cpiOnboarding.readDocuments.includes(
      "cpi-eastbourne-leaflet_sept-2022-a4.pdf"
    );

  const isManualHandlingComplete = cndData.manualHandling;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "cpi-onboarding") {
      return {
        ...section,
        status:
          isReadingListComplete && isManualHandlingComplete
            ? COMPLETED
            : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeRegattaOnboardingSection = (cndData) => {
  const hasReadings =
    cndData.regattaOnboarding &&
    (cndData.regattaOnboarding.readings ||
      cndData.regattaOnboarding.readDocuments);
  let isReadingListComplete = false;
  if (hasReadings) {
    const readingList =
      cndData.regattaOnboarding &&
      (Object.keys(cndData.regattaOnboarding.readings) ||
        cndData.regattaOnboarding.readDocuments);
    isReadingListComplete =
      readingList &&
      cndData.regattaOnboarding &&
      cndData.regattaOnboarding.YRG4 &&
      cndData.regattaOnboarding.YRG5 &&
      cndData.regattaOnboarding.YRG6 &&
      cndData.regattaOnboarding.YRG7 &&
      cndData.regattaOnboarding.YREG &&
      readingList.includes("uk-all-diversity-and-inclusion.pdf") &&
      readingList.includes("pp-drug-and-alcohol-policy-may-2024.pdf") &&
      readingList.includes("group-code-of-conduct-march-2024-2.pdf") &&
      readingList.includes("smart-device-policy-march-2023-v33.pdf") &&
      readingList.includes("management_of_locker_sign_off.pdf") &&
      readingList.includes("uk-all-gdpr-non-managers.pdf") &&
      readingList.includes("net2_communication_brief_v3.pdf") &&
      readingList.includes(
        "regatta-pioneer-point-hs-induction-manual-2022.pdf"
      ) &&
      readingList.includes("regatta-values-framework.pdf");
  }

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "regatta-onboarding") {
      return {
        ...section,
        status: isReadingListComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completePecanOnboardingSection = (cndData) => {
  const isReadingListComplete =
    cndData.pecanOnboarding &&
    cndData.pecanOnboarding.readDocuments &&
    cndData.pecanOnboarding.readDocuments.includes("iduction-booklet.pdf") &&
    cndData.pecanOnboarding.readDocuments.includes("hygiene-regulations.pdf") &&
    cndData.pecanOnboarding.readDocuments.includes(
      "food-hygiene-rules-copy.pdf"
    );

  const isInductionSubmitted =
    cndData.pecanOnboarding &&
    cndData.pecanOnboarding.forms &&
    cndData.pecanOnboarding.forms.submittedInduction;

  const isAssessmentSubmitted =
    cndData.pecanOnboarding &&
    cndData.pecanOnboarding.forms &&
    cndData.pecanOnboarding.forms.submittedAssessment;

  const isProfileImage = cndData.profileImage;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "onboarding-pecan") {
      return {
        ...section,
        status:
          isReadingListComplete &&
          isInductionSubmitted &&
          isAssessmentSubmitted &&
          isProfileImage
            ? COMPLETED
            : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completePusterlaOnboardingSection = (cndData) => {
  const allDocNames = [
    "modern-slavery-statement.pdf",
    "pusterla-pollards-welcome-handbook-hrgo.pdf",
    "pusterla-pollards-induction-2022.pdf",
    "pusterla-pollards-manual-handling-2022.pdf",
    "pusterla-pollards-safety-knives.pdf",
    "pusterla-pollards-support-staff-anti-bribery-corruption-2022.pdf",
    "pusterla-pollards-support-staff-ethics-2022.pdf",
    "pusterla-pollards-support-staff-human-rights-2022.pdf",
    "raising-concerns-and-grievances-pusterla-pollards.pdf",
    "sow-no-29-glue-lines-march-22.pdf",
  ];

  const readingList =
    cndData.pusterlaOnboarding && cndData.pusterlaOnboarding.readDocuments;

  const hasAssessmentFile =
    cndData.pusterlaOnboarding && cndData.pusterlaOnboarding.YPUP;

  const score = cndData.pusterlaOnboarding && cndData.pusterlaOnboarding.score;

  let isReadingListComplete = false;

  if (readingList) {
    isReadingListComplete = allDocNames.every((docName) =>
      readingList.includes(docName)
    );
  }
  const assessmentComplete = score === 5 && hasAssessmentFile;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "pusterla-onboarding") {
      return {
        ...section,
        status:
          isReadingListComplete && assessmentComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeRoyalMailOnboardingSection = (cndData) => {
  const royalMailFormsComplete =
    cndData.royalMailOnboarding &&
    cndData.royalMailOnboarding.submitted &&
    // additional condition for old candidates to fill new form
    cndData.royalMailOnboarding.addSF &&
    cndData.royalMailOnboarding.traffic;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "royal-mail-onboarding") {
      return {
        ...section,
        status: royalMailFormsComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeDnataOnboardingSection = (cndData) => {
  const onboarding = cndData.dnataOnboarding;
  const extraFormsList = [
    "YDN5",
    "YDN6",
    "YDN7",
    "YDN7",
    "YDN8",
    "YDN9",
    "YDN0",
  ];

  const dnataFormComplete =
    onboarding &&
    onboarding.submitted &&
    onboarding.testCompleted &&
    onboarding.dnataDeclaration;

  const extraFormsListComplete =
    onboarding && extraFormsList.every((formName) => formName in onboarding);

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "dnata-onboarding") {
      return {
        ...section,
        status:
          dnataFormComplete && extraFormsListComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeSwissportOnboardingSection = (cndData) => {
  const swissportFormComplete =
    cndData.swissportOnboarding &&
    cndData.swissportOnboarding.fiveYearHistory &&
    cndData.swissportOnboarding.employerHealthQuestionnaire &&
    cndData.swissportOnboarding.personalInformationForm &&
    cndData.swissportOnboarding.informationReleaseForm;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "swissport-onboarding") {
      return {
        ...section,
        status: swissportFormComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeNHSReferencingFormSection = (cndData) => {
  const nhsFormComplete = cndData.nhsReferenceForm;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "nhs-referencing") {
      return {
        ...section,
        status: nhsFormComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeUmbrellaDeclarationFormSection = (cndData) => {
  const declarationFormComplete =
    cndData.umbrellaDeclarationForm &&
    cndData.umbrellaDeclarationForm.completed;
  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "umbrella-declaration") {
      return {
        ...section,
        status: declarationFormComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeGateOnboardingSection = (cndData) => {
  const gateFormComplete =
    cndData.gateOnboarding &&
    cndData.gateOnboarding.declaration &&
    cndData.gateOnboarding.workhistory &&
    cndData.gateOnboarding.application &&
    cndData.gateOnboarding.referenceeducation;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "gate-group-onboarding") {
      return {
        ...section,
        status: gateFormComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeABMOnboardingSection = (cndData) => {
  const abmFormComplete =
    cndData.abmOnboarding &&
    cndData.abmOnboarding.submitted &&
    cndData.abmOnboarding.abmDeclaration &&
    cndData.abmOnboarding.abmDeclaration2;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "ABM-onboarding") {
      return {
        ...section,
        status: abmFormComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeDrivingOnboardingSection = (cndData) => {
  const drivingOnboardingComplete = Boolean(
    cndData.drivingOnboarding && cndData.drivingOnboarding.submitted
  );
  const isnonHGVPassed = cndData.nonHGVPassed;
  const isDriversAssessmentPassed = cndData.driverAssessmentPassed;
  const areAllDocsUploaded =
    cndData.documentationFiles &&
    cndData.documentationFiles.filter(
      (e) =>
        e.TypeCode === "JPDL" ||
        e.TypeCode === "JDLB" ||
        e.TypeCode === "JCPC" ||
        e.TypeCode === "JCPB" ||
        e.TypeCode === "JDIG"
    ).length === 5;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "driving-onboarding") {
      return {
        ...section,
        status:
          drivingOnboardingComplete &&
          areAllDocsUploaded &&
          isnonHGVPassed &&
          isDriversAssessmentPassed
            ? COMPLETED
            : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeMolecularOnboardingSection = (cndData) => {
  const molecularOnboardingComplete =
    cndData.molecularOnboarding &&
    cndData.molecularOnboarding.YML1 &&
    cndData.molecularOnboarding.YML2 &&
    cndData.molecularOnboarding.YML3 &&
    cndData.molecularOnboarding.readDocuments &&
    cndData.molecularOnboarding.readDocuments.includes(
      "mpl-induction-booklet.pdf"
    ) &&
    cndData.molecularOnboarding.readDocuments.includes(
      "hr-45-alcohol-and-drug-misuse-policyissue-1august-2017.pdf"
    );

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "molecular-onboarding") {
      return {
        ...section,
        status: molecularOnboardingComplete ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeCompleatFoodOnboardingSection = (cndData) => {
  const compleatFood = cndData.compleatFoodOnboarding;
  const readingList = [
    "kronos-how-to-request-and-view-holiday.pdf",
    "employee-handbook-tottle-27042022.pdf",
    "working-time-opt-out-form.pdf",
    "unplanned-absence-reminder.pdf",
  ];

  const formsList = ["YCF1", "YCF3", "YCF4", "YCF5"];

  const isReadingListComplete =
    compleatFood &&
    compleatFood.readDocuments &&
    readingList.every((docName) =>
      compleatFood.readDocuments.includes(docName)
    );

  const isFormsListComplete =
    compleatFood && formsList.every((docName) => docName in compleatFood);

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "compleat-food-onboarding") {
      return {
        ...section,
        status:
          isReadingListComplete && isFormsListComplete
            ? COMPLETED
            : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeWoodlandOnboardingSection = (cndData) => {
  const woodland = cndData.woodlandGroupOnboarding;

  const isCompleted =
    woodland &&
    cndData.woodlandGroupOnboarding.fireSafety &&
    cndData.woodlandGroupOnboarding.handOuts &&
    cndData.woodlandGroupOnboarding.incidentReporting &&
    cndData.woodlandGroupOnboarding.machinery &&
    cndData.woodlandGroupOnboarding.policies &&
    cndData.woodlandGroupOnboarding.siteRules &&
    cndData.woodlandGroupOnboarding.training;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "woodland-group-onboarding") {
      return {
        ...section,
        status: isCompleted ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeGrowUpFarmsOnboardingSection = (cndData) => {
  const onboarding = cndData.growUpFarmsOnboarding;
  const readingList = [
    "farm-handbook.pdf",
    "food-safety-induction-v2.pdf",
    "hs-induction-employees-v2.pdf",
  ];
  const formsList = ["YGUF", "YGU2"];
  const isReadingListComplete =
    onboarding &&
    onboarding.readDocuments &&
    readingList.every((docName) => onboarding.readDocuments.includes(docName));
  const isFormsListComplete =
    onboarding && formsList.every((formName) => formName in onboarding);
  const isOnboardingCompleted = isReadingListComplete && isFormsListComplete;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "grow-up-farms-onboarding") {
      return {
        ...section,
        status: isOnboardingCompleted ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const completeDavinFoodsOnboardingSection = (cndData) => {
  const onboarding = cndData.davinFoodsOnboarding;
  const readingList = [
    "pr902-induction-food-safety-at-davin-foods-v1.pdf",
    "pr903-induction-allergen-awareness-v2.pdf",
    "pr904-induction-hs-overview-v1.pdf",
    "pr907-induction-manual-handling-v1.pdf",
    "pr905-induction-coshh-introduction-to-chemicals-and-safe-cleaning-practises-v1.pdf",
  ];

  const quizzesList = [
    "chemicalSafetyQuiz",
    "evacuationQuiz",
    "foodAllergensQuiz",
    "foodSafetyQuiz",
    "healthAndSafetyQuiz",
    "manualHandlingQuiz",
  ];
  const formsList = ["YDF7"];
  const isReadingListComplete =
    onboarding &&
    onboarding.readDocuments &&
    readingList.every((docName) => onboarding.readDocuments.includes(docName));
  const isFormsListComplete =
    onboarding && formsList.every((formName) => formName in onboarding);
  const isQuizzesListCompleted =
    onboarding &&
    quizzesList.every((quizName) => {
      if (quizName in onboarding) {
        const quiz = onboarding[quizName];
        const { status, retries } = quiz;
        return status === "Pass" || retries === 0;
      }
      return false;
    });

  const isOnboardingCompleted =
    isReadingListComplete && isFormsListComplete && isQuizzesListCompleted;

  const updatedSections = cndData.sections.map((section) => {
    if (section.link === "davin-foods-onboarding") {
      return {
        ...section,
        status: isOnboardingCompleted ? COMPLETED : NOT_STARTED,
      };
    }
    return section;
  });
  return {
    ...cndData,
    sections: updatedSections,
  };
};

export const handleCvStatus = (cndData) => {
  const hasWorkHistory = cndData.workHistory && cndData.workHistory.length > 0;
  const updatedSections = cndData.sections.map((section) => {
    if (section.taskName === "Cv") {
      if (hasWorkHistory || getByPath(section.fields[0], cndData)) {
        return {
          ...section,
          status: CONTINUAL,
        };
      }
      return {
        ...section,
        status: null,
      };
    }
    return section;
  });
  return { ...cndData, sections: updatedSections };
};

export const addPercentage = (cndData) => {
  const { filledSections, unfilledSections } = getSortedSections(
    cndData,
    () => {}
  );

  const percent = Math.floor(
    (filledSections.length /
      (filledSections.length + unfilledSections.length)) *
      100
  );

  return {
    ...cndData,
    profilePercent: percent,
  };
};
